import React, { useState } from "react";
import "./style.scss";
import SearchInputComponent from "../../../components/reusable-ui/search-input";
import { removeSpecialCharacters } from "../../../utils";
import { useDispatch } from "react-redux";
import { deleteTaxTypeData, getAllTaxTypesData } from "../../../redux/slices";
import { toastSuccess } from "../../../utils/react-toaster";
import { COMPONENT_MODES, CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";
import CtaComponent from "../../../components/reusable-ui/cta";
import emptyListPlaceholder from "../../../assets/images/empty-list-placeholder.svg";

interface Props {
  openTaxTypeView: any;
  taxTypeDataMap?: any;
  modeConfig?: any;
  setModeConfig?: any;
  taxTypeId?: string;
}

const TaxTypeViewComponent = (props: Props) => {
  const {
    taxTypeDataMap,
    taxTypeId,
    modeConfig,
    openTaxTypeView,
    setModeConfig,
  } = props;
  const dispatch = useDispatch();
  const [searchData, setSearchData] = useState<any>({
    value: "",
    results: [],
  });
  const [openDeleteTaxTypeAlert, setOpenDeleteTaxTypeAlert] = useState(false);
  const [curTaxTypeId, setCurTaxTypeId] = useState("");

  /**
   * On search input it will parse the taxTypeList and it will filter out all the tax types based on the search string on name of the tax type
   * @param searchStr
   */
  const onSearchInput = (searchStr: string) => {
    const results: any = [];
    if (searchStr && taxTypeDataMap) {
      for (let key of Object.keys(taxTypeDataMap)) {
        if (
          taxTypeDataMap[key].name
            .toLocaleLowerCase()
            .includes(searchStr.toLocaleLowerCase())
        ) {
          results.push(taxTypeDataMap[key]);
        }
      }
    }
    setSearchData({ value: searchStr, results });
  };

  const onTaxTypeClick = (taxTypeId: string = "") => {
    openTaxTypeView(taxTypeId);
  };

  const deleteTaxType = (mid: string) => {
    dispatch(
      deleteTaxTypeData({
        mid,
        callback: (isSuccess: Boolean) => {
          if (isSuccess) {
            toastSuccess("Tax Name Successfully deleted!");
            dispatch(getAllTaxTypesData());
            openTaxTypeView("");
            setCurTaxTypeId("");
            setOpenDeleteTaxTypeAlert(false);
          }
        },
      })
    );
  };

  const onAddButtonClick = () => {
    openTaxTypeView("");
    setModeConfig({ type: COMPONENT_MODES.addNewMode });
  };

  /**
   * This function will render the tax Types
   */
  function renderTaxTypeList(taxTypeList: any): React.JSX.Element {
    return (
      !!taxTypeList?.length &&
      taxTypeList.map((taxType: any, index: number) => {
        return (
          <div
            className={`taxType-item flex items-center py-[16px] relative cursor-pointer ${
              taxType?.mid === taxTypeId
                ? "bg-black/5 border-0 last:border-b-0 border-l-[6px] !border-black pl-[19px] pt-[17px]"
                : "pl-[24px] border border-[#ECECEC] border-b-0 last:border-b"
            }`}
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              onTaxTypeClick(taxType?.mid);
            }}
          >
            <h1
              className={`text-[18px] mr-[12px] capitalize ${
                taxType?.mid === taxTypeId ? "font-semibold" : ""
              }`}
            >
              {taxType.name}
            </h1>
            <div
              className="delete-icon rista-sprite ic-delete-grey absolute right-[24px] hidden"
              onClick={(e) => onDeleteIconClick(e, taxType.mid)}
            ></div>
          </div>
        );
      })
    );
  }

  function renderSearchResults(): React.JSX.Element {
    return searchData.results.length ? (
      <>
        <div className="text-14 font-normal pb-16 pl-40 opacity-50 border-b-[1px] border-[#ECECEC]">
          {searchData.results.length} results found
        </div>
        {renderTaxTypeList(searchData.results)}
      </>
    ) : (
      <div className="flex flex-col justify-center items-center w-full mt-56">
        <div className="flex items-center justify-center w-[72px] h-[72px] bg-opacity-5 bg-[#000] rounded-[8px]">
          <div className="rista-sprite ic-not-found"></div>
        </div>
        <p className="text-20 font-semibold mt-24 mb-6 opacity-40">
          No results found
        </p>
        <p className="text-13 font-normal mb-6 opacity-40 mt-[0px]">
          Please try using other name or keyword
        </p>
      </div>
    );
  }

  const onDeleteIconClick = (e: any, id: string) => {
    setOpenDeleteTaxTypeAlert(true);
    e.stopPropagation();
    setCurTaxTypeId(id);
  };

  const onDeleteAlertClose = () => {
    setOpenDeleteTaxTypeAlert(false);
    setCurTaxTypeId("");
  };

  return (
    <div className="relative h-full">
      {Object.values(taxTypeDataMap)?.length ? (
        <>
          <div className="action-container p-24 flex justify-between">
            <SearchInputComponent
              placeholder={"Search Tax Name"}
              customClass={"text-15 py-12"}
              onSerachInput={onSearchInput}
              searchMinLength={3}
              regexCheck={removeSpecialCharacters}
              elementWidthClass={"w-[60%]"}
            ></SearchInputComponent>
            <div className="flex border-[#7FB9FD] border-[1px] bg-white cursor-pointer rounded-[8px] items-center pr-4">
              <p
                className="flex text-blue text-16 font-semibold px-16 py-10"
                onClick={onAddButtonClick}
              >
                <span>+</span>&nbsp;Add
              </p>
            </div>
          </div>

          <div className="h-[calc(100vh-361px)] overflow-y-auto pb-[8px]">
            {searchData.value.length >= 3
              ? renderSearchResults()
              : renderTaxTypeList(
                  taxTypeDataMap ? Object.values(taxTypeDataMap) : []
                )}
          </div>
        </>
      ) : (
        <div className="flex flex-col justify-center items-center w-full p-24 text-center absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 ">
          <img src={emptyListPlaceholder} alt="" />
          <h1 className="mt-[14px] text-[#4B4B4B] text-20 mb-[16px] font-bold">
            Feels So Empty !
          </h1>
          <p className="text-[15px] text-black/70 mb-[28px]">
            You haven’t added any tax names. Click on ‘+ Add Tax Name’ to start
            adding tax names.
          </p>
          <CtaComponent
            ctaSize={CTA_SIZE.medium}
            ctaType={CTA_TYPE.active_cta}
            ctaText="+ Add Tax Name"
            onCtaClick={onAddButtonClick}
          />
        </div>
      )}

      {openDeleteTaxTypeAlert && (
        <ConfirmationModal
          onModalClose={onDeleteAlertClose}
          onConfirmBtnClick={() => deleteTaxType(curTaxTypeId)}
          headingText="Delete Tax Name"
          description="This is an irreversible action. Do you still want to continue?"
        />
      )}
    </div>
  );
};

export default TaxTypeViewComponent;
